import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UndoProvider } from './context/UndoContext';
import UndoToast from './components/UndoToast';
import ArchivePage from './pages/admin/ArchivePage';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AcceptInvitation from './pages/AcceptInvitation.tsx';
import SearchResults from './pages/SearchResults';
import InseratDetail from './pages/InseratDetail';
import InserateList from './pages/InserateList';
import VerfahrenList from './pages/VerfahrenList';
import VerfahrenDetail from './pages/VerfahrenDetail';
import VermieterList from './pages/VermieterList';
import VermieterDetail from './pages/VermieterDetail';
import GruppenList from './pages/GruppenList';
import GruppeDetail from './pages/GruppeDetail';
import AdressenList from './pages/AdressenList';
import AdresseDetail from './pages/AdresseDetail';
import HaeuserList from './pages/HaeuserList';
import HausDetail from './pages/HausDetail';
import ObjekteList from './pages/ObjekteList';
import ObjektDetail from './pages/ObjektDetail';
import AnfragenList from './pages/AnfragenList';
import AnfrageDetail from './pages/AnfrageDetail';
import Karte from './pages/Karte';
import Bebauungsplaene from './pages/Bebauungsplaene';
import BebauungsplanDetail from './pages/BebauungsplanDetail';
import Daten from './pages/Daten';
import Konfiguration from './pages/Konfiguration';
import Dashboard from './pages/Dashboard';
import Profil from './pages/Profil';
import AufgabenList from './pages/AufgabenList';
import CityList from './pages/admin/CityList';
import CityDetail from './pages/admin/CityDetail';
import ScrapeLog from './pages/admin/ScrapeLog';
import UserList from './pages/settings/UserList';
import UserDetail from './pages/settings/UserDetail';
import UserInvitations from './pages/settings/UserInvitations';
import Sidebar from './components/Sidebar';
import AppHeader from './components/AppHeader';
import { useModuleVisibility, firstModulePath } from './hooks/useConfigVisibility';
import type { UserRole } from './types/index.js';

const RouteSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,  // 5 Minuten
      gcTime: 1000 * 60 * 10,    // 10 Minuten
      retry: 1,
      throwOnError: false, // Don't throw errors, handle them in components
    },
    mutations: {
      retry: 0,
    },
  },
});

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Role-gated route wrapper
const RoleRoute = ({ roles, children }: { roles: UserRole[]; children: React.ReactNode }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const mv = useModuleVisibility(isAuthenticated);

  if (loading) return <RouteSpinner />;

  if (!isAuthenticated || !user) return <Navigate to="/login" />;
  if (!roles.includes(user.role)) return <Navigate to={firstModulePath(mv)} replace />;

  return <>{children}</>;
};

// Module-gated route wrapper: module_visibility is the master switch. A disabled
// module redirects to the first enabled module, so it's unreachable even by
// direct URL/bookmark. (system_admin has an empty map → all enabled.)
const ModuleRoute = ({ moduleKey, children }: { moduleKey: string; children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const mv = useModuleVisibility(isAuthenticated);

  if (loading) return <RouteSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (mv[moduleKey] === false) return <Navigate to={firstModulePath(mv)} replace />;

  return <>{children}</>;
};

const PublicTokenPageGuard = ({
  children,
  pageTitle,
}: {
  children: React.ReactNode;
  pageTitle: string;
}) => {
  const { isAuthenticated, loading, logout } = useAuth();
  const mv = useModuleVisibility(isAuthenticated);
  const navigate = useNavigate();

  if (loading) return <RouteSpinner />;

  if (!isAuthenticated) return <>{children}</>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full bg-white border border-gray-200 rounded-lg shadow p-6 space-y-4">
        <h1 className="text-xl font-semibold text-gray-900">Bereits angemeldet</h1>
        <p className="text-sm text-gray-700">
          Die Seite {pageTitle} ist nur fuer nicht angemeldete Nutzer vorgesehen.
        </p>
        <p className="text-sm text-gray-600">
          Bitte beachten Sie: Einladungs- und Passwort-Links enthalten sicherheitsrelevante Token und sollten nur in einem passenden Kontext genutzt werden.
        </p>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={() => {
              logout();
              navigate('/login', { replace: true });
            }}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            Abmelden
          </button>
          <button
            onClick={() => navigate(firstModulePath(mv), { replace: true })}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    </div>
  );
};

const CatchAllRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const mv = useModuleVisibility(isAuthenticated);

  if (loading) return <RouteSpinner />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={firstModulePath(mv)} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UndoProvider>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
        </UndoProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

const AppShell = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const isTokenPublicPage =
    location.pathname === '/reset-password' || location.pathname === '/accept-invitation';
  const hideChrome = isAuthenticated && isTokenPublicPage;

  return (
    <div className={hideChrome ? 'min-h-screen' : 'flex flex-col h-screen overflow-hidden'}>
      {!hideChrome && <AppHeader />}
      <div className={hideChrome ? '' : 'flex flex-1 min-h-0'}>
        {!hideChrome && <Sidebar />}
        <div className={hideChrome ? '' : 'flex-1 overflow-auto min-w-0'}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/reset-password"
              element={
                <PublicTokenPageGuard pageTitle="Passwort zuruecksetzen">
                  <ResetPassword />
                </PublicTokenPageGuard>
              }
            />
            <Route
              path="/accept-invitation"
              element={
                <PublicTokenPageGuard pageTitle="Einladung annehmen">
                  <AcceptInvitation />
                </PublicTokenPageGuard>
              }
            />
            <Route path="/dashboard" element={<ModuleRoute moduleKey="dashboard"><Dashboard /></ModuleRoute>} />
            <Route path="/aufgaben" element={<ModuleRoute moduleKey="aufgaben"><AufgabenList /></ModuleRoute>} />
            <Route path="/search/:searchId" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
            <Route path="/inserate" element={<ModuleRoute moduleKey="inserate"><InserateList /></ModuleRoute>} />
            <Route path="/verfahren" element={<ModuleRoute moduleKey="verfahren"><VerfahrenList /></ModuleRoute>} />
            <Route path="/verfahren/:verfahrenId" element={<ModuleRoute moduleKey="verfahren"><VerfahrenDetail /></ModuleRoute>} />
            <Route path="/vermieter" element={<ModuleRoute moduleKey="vermieter"><VermieterList /></ModuleRoute>} />
            <Route path="/vermieter/:vermieterId" element={<ModuleRoute moduleKey="vermieter"><VermieterDetail /></ModuleRoute>} />
            <Route path="/gruppen" element={<ModuleRoute moduleKey="gruppen"><GruppenList /></ModuleRoute>} />
            <Route path="/gruppen/:gruppeId" element={<ModuleRoute moduleKey="gruppen"><GruppeDetail /></ModuleRoute>} />
            <Route path="/adressen" element={<ModuleRoute moduleKey="adressen"><AdressenList /></ModuleRoute>} />
            <Route path="/adressen/:adresseId" element={<ModuleRoute moduleKey="adressen"><AdresseDetail /></ModuleRoute>} />
            <Route path="/haeuser" element={<ModuleRoute moduleKey="haeuser"><HaeuserList /></ModuleRoute>} />
            <Route path="/haeuser/:hausId" element={<ModuleRoute moduleKey="haeuser"><HausDetail /></ModuleRoute>} />
            <Route path="/objekte" element={<ModuleRoute moduleKey="objekte"><ObjekteList /></ModuleRoute>} />
            <Route path="/objekte/:objektId" element={<ModuleRoute moduleKey="objekte"><ObjektDetail /></ModuleRoute>} />
            <Route path="/inserat/:listingId" element={<ModuleRoute moduleKey="inserate"><InseratDetail /></ModuleRoute>} />
            <Route path="/anfragen" element={<ModuleRoute moduleKey="anfragen"><AnfragenList /></ModuleRoute>} />
            <Route path="/anfragen/:anfrageId" element={<ModuleRoute moduleKey="anfragen"><AnfrageDetail /></ModuleRoute>} />
            <Route path="/karte" element={<ModuleRoute moduleKey="karte"><Karte /></ModuleRoute>} />
            <Route path="/bebauungsplaene" element={<ModuleRoute moduleKey="bebauungsplaene"><Bebauungsplaene /></ModuleRoute>} />
            <Route path="/bebauungsplaene/:id" element={<ModuleRoute moduleKey="bebauungsplaene"><BebauungsplanDetail /></ModuleRoute>} />
            <Route path="/daten" element={<ModuleRoute moduleKey="daten"><Daten /></ModuleRoute>} />
            <Route path="/konfiguration" element={<ProtectedRoute><Konfiguration /></ProtectedRoute>} />
            <Route path="/profil" element={<ProtectedRoute><Profil /></ProtectedRoute>} />

            {/* system_admin: City CRUD */}
            <Route path="/admin/cities" element={<RoleRoute roles={['system_admin']}><CityList /></RoleRoute>} />
            <Route path="/admin/cities/:cityId" element={<RoleRoute roles={['system_admin']}><CityDetail /></RoleRoute>} />
            <Route path="/admin/scrape-log" element={<RoleRoute roles={['system_admin']}><ScrapeLog /></RoleRoute>} />

            {/* system_admin + city_admin: User management */}
            <Route path="/settings/users" element={<RoleRoute roles={['system_admin', 'city_admin']}><UserList /></RoleRoute>} />
            <Route path="/settings/users/invitations" element={<RoleRoute roles={['system_admin', 'city_admin']}><UserInvitations /></RoleRoute>} />
            <Route path="/settings/users/:userId" element={<RoleRoute roles={['system_admin', 'city_admin']}><UserDetail /></RoleRoute>} />

            {/* Soft-delete archive: city_admin or system_admin can restore */}
            <Route path="/admin/archiv" element={<RoleRoute roles={['system_admin', 'city_admin']}><ArchivePage /></RoleRoute>} />

            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="*" element={<CatchAllRoute />} />
          </Routes>
        </div>
      </div>
      {!hideChrome && <UndoToast />}
    </div>
  );
};

export default App;
